import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import Modal from "bootstrap/js/dist/modal";
import { useHeaderFilters } from "../../context/HeaderFilterContext";
// AvatarEditor provides an interactive canvas for users to crop, zoom, and rotate their uploaded image into a circular avatar
import AvatarEditor from "react-avatar-editor";
import Button from "antd/es/button";
import { FaCamera as RawFaCamera, FaInfoCircle as RawFaInfo } from "react-icons/fa";
import { Tab, Nav } from "react-bootstrap";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import nodeApi from "../../nodeApi";
import dayjs from "dayjs";

// Cast RawFaCamera to an SVG component so TypeScript allows JSX usage
const FaCamera = RawFaCamera as React.FC<React.SVGProps<SVGSVGElement>>;
const FaInfoCircle = RawFaInfo as React.FC<React.SVGProps<SVGSVGElement>>;

interface Props {
  employeeId?: number | string;
  skipSaveApiCall?: boolean;
  name?: string;
  designation?: string;
  imageUrl?: string;
  // If provided, use parent‑supplied onboarding instead of
  // fetching it again here. This avoids duplicate API calls.
  onboardingData?: OnboardingResponse | null;
  onboardingMarkedCompleteProp?: boolean;
  onOnboardingRefresh?: () => Promise<void> | void;
  onOnboardingComplete?: (password?: string) => void;
  password?: string;
  employmentStatus?: string;
  isActiveEmployee?: boolean;
}

interface Breadcrumb {
  title: string;
  path?: string;
  onClick?: () => void;
}
interface DropdownOption {
  id: string;
  name: string;
}
interface Statuses {
  id: string;
  name: string;
}
interface BankDetails {
  account_number: string;
  account_name: string;
  branch_name: string;
  ifsc: string;
}
interface UserRole {
  id: string;
  name: string;
}
interface Gender {
  id: string;
  name: string;
}
interface Designations {
  id: string;
  name: string;
}
interface ReportingHead {
  id: string;
  name: string;
}
interface EmployeeData {
  id: string;
  name: string;
  designation?: { title: string };
  image_url?: string;
  date_of_birth?: string;
  date_of_joining?: string;
  bank_details?: {
    account_number?: string;
    account_name?: string;
    branch_name?: string;
    ifsc?: string;
  };
}
interface OnboardingStep {
  key: string;
  label: string;
  completed: boolean;
  mandatory: boolean;
  count?: number;
  required?: number;
  locked?: boolean;
  value?: string;
}
interface OnboardingSummary {
  mandatorySteps: number;
  completedMandatory: number;
  isComplete: boolean;
  onboardingMarkedComplete?: boolean;
}

interface OnboardingResponse {
  employeeId: number;
  steps: OnboardingStep[];
  summary: OnboardingSummary;
}

interface ExitSummary {
  mandatorySteps: number;
  completedMandatory: number;
  isComplete: boolean;
  isExitComplete?: boolean;
  dateOfLeaving?: string;
  status?: number | string;
  isReleased?: boolean;
  isActive?: boolean;
}

interface ExitResponse {
  employeeId: number;
  steps: OnboardingStep[];
  summary: ExitSummary;
}
interface InductionItem {
  id: number;
  title: string;
  description: string;
  completed: boolean;
}
interface AccessRevokeItem {
  id: number;
  title: string;
  completed: boolean;
}
interface ExitProps extends Props {
  exitData?: ExitResponse | null;
}

const EmployeeImage: React.FC<ExitProps> = ({
  employeeId: propEmployeeId,
  skipSaveApiCall = false,
  name: passedName,
  designation: passedDesignation,
  imageUrl: passedImageUrl,
  onboardingData,
  onboardingMarkedCompleteProp,
  onOnboardingRefresh,
  onOnboardingComplete,
  password,
  employmentStatus,
  exitData,
  isActiveEmployee: propIsActiveEmployee,
}) => {
  const { user, setUser } = useAuth();
  const { id: paramEmployeeId = "" } = useParams<{ id: string }>();
  const employeeId = propEmployeeId || paramEmployeeId;
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 767);
  const { setFilters } = useHeaderFilters();
  const location = useLocation();
  const modalInstance = useRef<Modal | null>(null);
  const [inductionData, setInductionData] = useState<InductionItem[]>([]);
  const [localInductionData, setLocalInductionData] = useState<InductionItem[]>([]);
  const [isSubmittingInduction, setIsSubmittingInduction] = useState(false);
  const [inductionLoading, setInductionLoading] = useState(false);
  
  const [accessRevokeData, setAccessRevokeData] = useState<AccessRevokeItem[]>([]);
  const [localAccessRevokeData, setLocalAccessRevokeData] = useState<AccessRevokeItem[]>([]);
  const [isSubmittingAccessRevoke, setIsSubmittingAccessRevoke] = useState(false);
  const [accessRevokeLoading, setAccessRevokeLoading] = useState(false);
  // state
  const [employee, setEmployee] = useState<EmployeeData | null>(null);
  //   const [imageUrl, setImageUrl] = useState<string>("")
  // const [imageUrl, setImageUrl] = useState<string>(propImageUrl || "");
  const [imageUrl, setImageUrl] = useState<string>(passedImageUrl || "");
  const [imageLoaded, setImageLoaded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [designations, setDesignations] = useState<DropdownOption[]>([]);
  // crop state
  const [error, setError] = useState("");
  const [genders, setGenders] = useState<DropdownOption[]>([]);
  const [statuses, setStatuses] = useState<DropdownOption[]>([]);
  const [userRoles, setUserRoles] = useState<DropdownOption[]>([]);
  const [reportingHeads, setReportingHeads] = useState<DropdownOption[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scale, setScale] = useState<number>(1);
  const editorRef = useRef<AvatarEditor>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingStep[]>([]);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingSummary, setOnboardingSummary] =
    useState<OnboardingSummary | null>(null);
  const [onboardingMarkedComplete, setOnboardingMarkedComplete] =
    useState<boolean>(false);
  const [canLoadOnboardingApis, setCanLoadOnboardingApis] =
    useState<boolean>(true);
  const [employeeStatus, setEmployeeStatus] = useState<string | null>(
    employmentStatus || null
  );

  // Exit workflow state (Notice Period only)
  const [exitSteps, setExitSteps] = useState<OnboardingStep[]>([]);
  const [exitSummary, setExitSummary] = useState<ExitSummary | null>(null);
  const [exitLoading, setExitLoading] = useState<boolean>(false);
  const [roleName, setRoleName] = useState<string>("");
  let bsModal: Modal | null = null;

  // breadcrumbs from location.state
  const locState = (location.state || {}) as {
    from?: string;
    payrollUrl?: string;
    attUrl?: string;
    leaveUrl?: string;
    monthYear?: string;
    previousUrl?: string;
    employeeName?: string;
  };
  const {
    from,
    payrollUrl,
    monthYear,
    previousUrl,
    employeeName,
    attUrl,
    leaveUrl,
  } = locState;
  const safePreviousUrl = previousUrl || "/employees";
  const safePayrollUrl = payrollUrl || "/payroll";
  const safeAttlUrl = attUrl || "/attendance-view/A";
  const safeLeaveUrl = leaveUrl || "/leave/leave-request";
  const safeMonthYear = monthYear || "";
  const safeEmployeeName = employeeName || "";

  const visibleOnboardingSteps = onboardingSteps.filter((step) => {
    if (employmentStatus === "5") {
      const isInv =
        typeof step.key === "string" &&
        step.key.toLowerCase().includes("inventory");
      const isPay = step.key === "payroll";
      return !isInv && !isPay;
    }
    return true;
  });

  const totalOnboardingSteps = visibleOnboardingSteps.length;

  const isStepVisuallyCompleted = (step: OnboardingStep) => {
    const isInventoryStep =
      typeof step.key === "string" &&
      step.key.toLowerCase().includes("inventory");

    // For inventory assignment, treat completion as purely manual
    // (based on step.completed) and ignore automatic counts.
    if (isInventoryStep) {
      return step.completed;
    }

    const hasQuantitativeCount =
      typeof step.count === "number" &&
      typeof step.required === "number" &&
      step.required > 0;

    // If backend marks a step as completed explicitly, always respect that
    if (step.completed) {
      return true;
    }

    return hasQuantitativeCount ? step.count === step.required : false;
  };

  const getStepColor = (step: OnboardingStep) => {
    const visuallyCompleted = isStepVisuallyCompleted(step);
    
    if (visuallyCompleted) return "#22c55e"; // green
    return step.mandatory ? "#ef4444" : "#9ca3af"; // red for required, grey for optional
  };

  const segmentWidthPercent =
    totalOnboardingSteps > 1 ? 100 / (totalOnboardingSteps - 1) : 0;

  const isNoticePeriod = () => {
    const status = employmentStatus || employeeStatus;
    return status === "6";
  };

  // Treat the employee as being in the exit flow whenever
  // we have exit summary data, even if the dropdown status
  // in the parent UI has not reloaded yet.
  const isInExitFlow = () => {
    if (exitSummary) return true;
    return isNoticePeriod();
  };

  // When parent passes onboarding data, hydrate local state from it so
  // this component stays purely presentational and does not re‑hit the
  // onboarding API again.
  useEffect(() => {
    if (onboardingData) {
      setOnboardingSteps(onboardingData.steps || []);
      setOnboardingSummary(onboardingData.summary || null);
    }
  }, [onboardingData]);

  useEffect(() => {
    if (typeof onboardingMarkedCompleteProp === "boolean") {
      setOnboardingMarkedComplete(onboardingMarkedCompleteProp);
    }
  }, [onboardingMarkedCompleteProp]);

  // When parent passes exit data (profile page), hydrate exit state from it.
  useEffect(() => {
    if (!skipSaveApiCall || !exitData) return;
    setExitSteps(exitData.steps || []);
    setExitSummary(exitData.summary || null);
    fetchAccessRevokeChecklist();
  }, [skipSaveApiCall, exitData]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load logged-in user role from localStorage, same pattern as profile.tsx
  useEffect(() => {
    const userString = localStorage.getItem("user");
    const storedUser = userString ? JSON.parse(userString) : null;
    setRoleName(
      storedUser && storedUser.role_name != null
        ? String(storedUser.role_name)
        : ""
    );
  }, []);

  // build breadcrumbs array
  const breadcrumbs: Breadcrumb[] =
    from === "payroll"
      ? [
          {
            title: "Payroll",
            onClick: () =>
              navigate(safePayrollUrl, {
                state: { from: "payroll", monthYear },
                replace: true,
              }),
          },
          ...(safeMonthYear
            ? [
                {
                  title: safeMonthYear,
                  onClick: () =>
                    navigate(safePayrollUrl, {
                      state: { from: "payroll", monthYear },
                      replace: true,
                    }),
                },
              ]
            : []),

          { title: employeeName || employee?.name || "Profile" },
        ]
      : from === "attendance"
      ? [
          {
            title: "Attendance",
            onClick: () =>
              navigate(safeAttlUrl, {
                state: { from: "attendance" },
                replace: true,
              }),
          },
          { title: employeeName || employee?.name || "Profile" },
        ]
      : from === "leave"
      ? [
          {
            title: "Leave",
            onClick: () =>
              navigate(safeLeaveUrl, {
                state: { from: "leave" },
                replace: true,
              }),
          },
          { title: employeeName || employee?.name || "Profile" },
        ]
      : from === "employees"
      ? [
          {
            title: "Employee",
            onClick: () => navigate(safePreviousUrl, { replace: true }),
          },
          {
            title: employeeName || employee?.name || "Profile",
          },
        ]
      : [
          {
            title: localStorage.getItem("pageTitle") || "Profile",
          },
        ];

  useEffect(() => {
    if (skipSaveApiCall) {
      setEmployee({
        id: "", // required by type
        name: passedName || "",
        designation: { title: passedDesignation || "" },
        image_url: passedImageUrl || "",
      });

      if (employmentStatus) {
        setEmployeeStatus(String(employmentStatus));
      }

      setImageUrl(passedImageUrl || "");
      setLoading(false);
      return;
    }

    const formatDate = (date: string | undefined) =>
      date ? moment(date, "DD-MM-YYYY").format("DD-MMM-YYYY") : "";

    const formatLeavingDate = (date: string | undefined) =>
      date ? moment(date, "YYYY-MM-DD").format("DD-MMM-YYYY") : "";

    const fetchEmployee = async () => {
      try {
        const res = await axiosInstance.get(`/edit-employee/${employeeId}`);

        if (res.status === 429) {
          window.location.href = "/internalserver";
          return;
        }

        const data = res.data;
        const emp = data.data;

        setEmployee({
          ...emp,
          date_of_birth: formatDate(emp.date_of_birth),
          date_of_joining: formatDate(emp.date_of_joining),
          date_of_leaving: formatLeavingDate(emp.date_of_leaving),
          permanent_wfh: !!emp.is_permenant_wfh,
          by_pass_attendance: !!emp.by_pass_attendance_check,
          gender: emp.gender,
          user_role: emp.role_name,
          bank_details: emp.bank_details || {},
        });
        setImageUrl(emp.image_url || "");
        const designations: Designations[] = Object.entries(
          res.data.designations || {}
        ).map(([id, name]) => ({
          id,
          name: name as string,
        }));
        setDesignations(designations);
        const reportingHeads: ReportingHead[] = Object.values(
          res.data.reporting_head || {}
        ).map((emp: any) => ({
          id: emp.id,
          name: emp.name,
        }));
        setReportingHeads(reportingHeads);
        const genders: Gender[] = Object.entries(res.data.gender || {}).map(
          ([id, name]) => ({
            id,
            name: name as string,
          })
        );
        setGenders(genders);
        const statuses: Statuses[] = Object.entries(res.data.status || {}).map(
          ([id, name]) => ({
            id,
            name: name as string,
          })
        );
        setStatuses(statuses);

        // Determine if this employee is in an exit status
        const statusMap = res.data.status || {};
        const statusId =
          emp.status !== undefined && emp.status !== null
            ? String(emp.status)
            : "";
        const statusLabel = statusMap[statusId] || "";
        setEmployeeStatus(statusId || null);
        const isExitStatus = ["Notice Period", "Released", "Terminated"].includes(
          statusLabel
        );
        setCanLoadOnboardingApis(!isExitStatus);
        const userRoles: UserRole[] = Object.entries(
          res.data.userrole || {}
        ).map(([id, name]) => ({
          id,
          name: name as string,
        }));
        setUserRoles(userRoles);
      } catch (err: any) {
        if (err.response) {
          if (err.response.status === 403) {
            localStorage.removeItem("token");
            window.location.href = "/";
            return;
          }
          if (err.response.status === 404) {
            navigate("/employee", {
              state: { error: "Employee not found" },
            });
            return;
          }
        }
        setError("Failed to load employee details.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [
    employeeId,
    navigate,
    skipSaveApiCall,
    passedName,
    passedDesignation,
    passedImageUrl,
  ]);

  const fetchInductionChecklist = async () => {
    if (!employeeId) return;

    try {
      setInductionLoading(true);

      const res = await nodeApi<any>(
        "/employee/induction-checklist",
        {
          method: "POST",
          body: { userId: employeeId },
        }
      );

      const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setInductionData(items);
      setLocalInductionData(items);

      // If induction is fully complete and we haven't yet triggered
      // the policy email for this employee, call the induction-update
      // API once so the backend can fire the email.
      const summary: any = (res as any).summary;
      let allCompleted = false;

      if (
        summary &&
        typeof summary.completedActive === "number" &&
        typeof summary.active === "number" &&
        summary.active > 0
      ) {
        allCompleted = summary.completedActive === summary.active;
      } else {
        allCompleted =
          items.length > 0 && items.every((item: InductionItem) => item.completed);
      }
      if (allCompleted) {
        const lastItem = items[items.length - 1];

        try {
          await nodeApi("/employee/induction-update", {
            method: "POST",
            body: {
              userId: employeeId,
              checklistId: lastItem.id,
              value: true,
            },
          });
        } catch (err) {
          console.error(
            "Error auto-triggering policy induction email",
            err
          );
        }
      }
    } catch (error) {
      console.error("Error fetching induction checklist", error);
    } finally {
      setInductionLoading(false);
    }
  };

  const fetchAccessRevokeChecklist = async () => {
    if (!employeeId) return;

    try {
      setAccessRevokeLoading(true);

      const res = await nodeApi<any>("/employee/access-revoke-checklist", {
        method: "POST",
        body: { userId: employeeId },
      });

      const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setAccessRevokeData(items);
      setLocalAccessRevokeData(items);
    } catch (error) {
      console.error("Error fetching access revoke checklist", error);
    } finally {
      setAccessRevokeLoading(false);
    }
  };

  const fetchOnboarding = async () => {
    // When parent owns onboarding, do not fetch here.
    if (skipSaveApiCall) return;

    if (!employeeId) return;

    try {
      setOnboardingLoading(true);

      const res = await nodeApi<OnboardingResponse>(
        "/employee/onboarding",
        {
          method: "POST",
          body: {
            userId: employeeId,
          },
        }
      );

      setOnboardingSteps(res?.steps || []);
      setOnboardingSummary(res?.summary || null);

      const serverMarked = !!res.summary?.onboardingMarkedComplete;
      const localKey = `onboardingComplete:${res.employeeId}`;
      const localMarked = localStorage.getItem(localKey) === "1";
      setOnboardingMarkedComplete(serverMarked || localMarked);
    } catch (err) {
      console.error("Onboarding error", err);
    } finally {
      setOnboardingLoading(false);
    }
  };

  useEffect(() => {
    if (skipSaveApiCall) return;
    if (employeeId && canLoadOnboardingApis) {
      fetchOnboarding();
    }
  }, [employeeId, canLoadOnboardingApis, skipSaveApiCall]);

  useEffect(() => {
    if (employeeId && canLoadOnboardingApis) {
      fetchInductionChecklist();
    }
  }, [employeeId, canLoadOnboardingApis, onboardingData]);

const fetchExitProgress = async () => {
    if (!employeeId) return;
    // Notice Period, or Released/Terminated reached directly without
    // completing the checklist first (exitSummary already populated
    // from the parent) both count as "in the exit flow" here.
    if (!isInExitFlow()) {
      setExitSteps([]);
      setExitSummary(null);
      return;
    }

    try {
      setExitLoading(true);
      const res = await nodeApi<ExitResponse>("/employee/exit", {
        method: "POST",
        body: { userId: employeeId },
      });

      setExitSteps(res?.steps || []);
      setExitSummary(res?.summary || null);

      if (res?.summary?.status != null) {
        setEmployeeStatus(res.summary.status.toString());
      }

      fetchAccessRevokeChecklist();
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load exit progress";

      // Show specific toast when LWD / Date of Leaving is missing
      if (
        status === 400 &&
        typeof message === "string" &&
        message.toLowerCase().includes("date of leaving")
      ) {
        toast.error(message);
      } else {
        console.error("Exit progress error", err);
        toast.error(message);
      }

      setExitSteps([]);
      setExitSummary(null);
    } finally {
      setExitLoading(false);
    }
  };

  useEffect(() => {
    if (skipSaveApiCall) return;
    if (!employeeId) return;
    if (!isNoticePeriod()) {
      setExitSteps([]);
      setExitSummary(null);
      return;
    }

    fetchExitProgress();
  }, [employeeId, employmentStatus, employeeStatus]);
  useEffect(() => {
    const ProfileMobileFilters = () => (
      <div className="px-2 px-md-2">
        <div className="designation-filters d-flex align-items-center flex-wrap ">
          <div className="d-flex align-items-center gap-2  toggle-att-employeeSalaryMedia">
            {/* Breadcrumb */}
            <nav
              aria-label="breadcrumb"
              className="mt-3 text-sm text-gray-600 toggle-att-employeeSalary"
            >
              <ol className="breadcrumb">
                {breadcrumbs.map((crumb, idx) => (
                  <li
                    key={idx}
                    className={`breadcrumb-item ${
                      idx === breadcrumbs.length - 1 ? "active" : ""
                    }`}
                    onClick={crumb.onClick}
                    style={{ cursor: crumb.onClick ? "pointer" : "default" }}
                  >
                    {crumb.title}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      </div>
    );
    const ProfileDesktopFilters = () => (
      <div className="px-2 px-md-2">
        <div className="designation-filters d-flex align-items-center flex-wrap ">
          <div className="d-flex align-items-center gap-2  toggle-att-employeeSalarymedia toggle-att-employeeSalarydesktop">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mt-3 text-sm text-gray-600">
              <ol className="breadcrumb">
                {breadcrumbs.map((crumb, idx) => (
                  <li
                    key={idx}
                    className={`breadcrumb-item ${
                      idx === breadcrumbs.length - 1 ? "active" : ""
                    }`}
                    onClick={crumb.onClick}
                    style={{ cursor: crumb.onClick ? "pointer" : "default" }}
                  >
                    {crumb.title}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      </div>
    );

    setFilters(
      isMobileView ? <ProfileMobileFilters /> : <ProfileDesktopFilters />
    );
    return () => {
      console.log("cleaning up filters");
      setFilters(null);
    };
  }, [isMobileView]);

  const openCropModal = () => {
    if (!modalRef.current) return;
    // create or re-use the Modal instance
    modalInstance.current = new Modal(modalRef.current);
    modalInstance.current.show();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      openCropModal();
    }
  };

  const handleSaveCrop = async () => {
    if (!editorRef.current) return;

    try {
      // 1. Extract the cropped image as a data URL
      const canvasDataUrl = editorRef.current
        .getImageScaledToCanvas()
        .toDataURL();

      // 2. Convert to Blob so we can append it to FormData
      const blob = await (await fetch(canvasDataUrl)).blob();

      // 3. Build FormData payload
      const formData = new FormData();
      formData.append("profile_image", blob, "avatar.png");
      formData.append("id", String(employeeId));

      // 4. POST to your save-image endpoint
      const res = await axiosInstance.post<{
        message: string;
        file_path: string;
        original_size: number;
        compressed_size: number;
      }>("/save-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // res.data now contains your JSON
      const { message, file_path } = res.data;

      // show the API‐returned message
      toast.success(message);

      // if you prefer to use the server’s returned file URL:
      setImageUrl(file_path);
      //  Get the logged-in user
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const loggedInUser = JSON.parse(storedUser);

      //  Only update if the logged-in user is updating their own image
      if (String(loggedInUser.id) === String(employeeId)) {
        loggedInUser.image_url = file_path;
        localStorage.setItem("user", JSON.stringify(loggedInUser));

        //  Also update context (if using context)
        if (user) {
          const updatedUser = { ...user, image_url: file_path };
          setUser(updatedUser);
        }
      }
    }
      // hide the modal
      modalInstance.current?.hide();
    } catch (err: any) {
      toast.error(err.message || "Something went wrong while saving image.");
    }
  };

  //   if (loading)
  //     return <div className="text-center py-4">Loading profile...</div>;

  return (
    <>
      <div className="card-height1">
        {loading ? (
          <div className="card mb-4 border-0">
            <div className="card-body text-center card-height card-height1">
              <div
                className="placeholder rounded-circle "
                style={{ width: 120, height: 120 }}
              />

              {/* <div className="mt-2">Loading profile...</div> */}
            </div>
          </div>
        ) : (
          // {/* Profile Header + Crop */}
          <div className="card mb-4 border-0">
            <div
              className="card-body text-center"
              style={{ minHeight: 211.56 }} // ← set minimum fixed height to prevent shrinking
            >
              <div
                className="position-relative d-inline-block"
                style={{ width: 120, height: 120 }} // ensure consistent image wrapper height
              >
                {!imageLoaded && (
                  <div
                    className="placeholder rounded-circle bg-light"
                    style={{ width: 120, height: 120 }}
                  />
                )}
                <img
                  src={imageUrl || "/default-avatar.png"}
                  className="rounded-circle"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    display: imageLoaded ? "block" : "none",
                  }}
                  alt={employee?.name || passedName}
                  onLoad={() => setImageLoaded(true)}
                />
                <label
                  htmlFor="file-input"
                  className="position-absolute bottom-0 end-0 bg-white rounded-circle p-2"
                  style={{ cursor: "pointer" }}
                >
                  <FaCamera />
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>

                <>
                  <h5 className="mt-2">{employee?.name || passedName}</h5>
              <small className="text-muted d-block">
                    {employee?.designation?.title || passedDesignation}
                  </small>
                  {(() => {
                    const lwdStep = exitSteps.find(
                      (s) => s.key === "last_working_day"
                    );
                    const rawValue =
                      lwdStep?.value || exitSummary?.dateOfLeaving || "";
                    if (!rawValue) return null;
                    const formatted = dayjs(rawValue).isValid()
                      ? dayjs(rawValue).format("DD-MMM-YYYY")
                      : rawValue;
                    return (
                      <small className="text-muted d-block mt-1">
                        Last Working Day:&nbsp;{formatted}
                      </small>
                    );
                  })()}
                  <div className="mt-2">
                    {roleName === "1" && exitSummary && (
                      exitSummary.isExitComplete ? null : (
                        <span className="badge px-3 py-2 bg-warning text-dark">
                          Exit Process In Progress
                        </span>
                      )
                  )}
                    {roleName === "1" &&
                      !exitSummary &&
                      onboardingSummary && (
                        (onboardingMarkedComplete || 
                          onboardingSummary.onboardingMarkedComplete || 
                          onboardingSummary.isComplete) 
                            ? null  /* Return actual null to render NOTHING */
                            : (
                              <span className="badge px-3 py-2 bg-danger">
                                Profile Incomplete
                              </span>
                            )
                      )}
                  </div>

                  {/* Exit workflow – driven by exitSummary.
                      Once exit is fully completed, hide the stages and
                      "Mark as Exited" controls and rely on the header badge. */}
                 {roleName === "1" && isInExitFlow() && (
  <div className="mt-4 px-3">
    {exitLoading && exitSteps.length === 0 ? (
      <div className="text-muted small mb-2">Loading exit stages…</div>
    ) : null}
    {exitSteps.length > 0 && !exitSummary?.isExitComplete && (
    (() => {
      const visibleExitSteps = exitSteps.filter(
        (s) => s.key !== "last_working_day"
      );
      const totalExitSteps = visibleExitSteps.length;

      if (totalExitSteps === 0) return null;

      const segmentWidth =
  totalExitSteps > 1 ? 100 / (totalExitSteps - 1) : 0;

      return (
    <div
  className="position-relative"
  style={{
    overflowX: isMobileView ? "auto" : "visible",
    paddingBottom: 6,
  }}
>
      <div
    className="d-flex align-items-start flex-nowrap"
    style={{
     gap: isMobileView ? 16 : 0,
      minWidth: isMobileView ? "max-content" : "100%",
      position: "relative",
    }}
  >
      {/* Base neutral line across full width */}
      <div
  style={{
    position: "absolute",
    top: 21,
    left: isMobileView ? 20 : 0,
    right: isMobileView ? 20 : 0,
    height: 2,
    backgroundColor: "#dee2e6",
    zIndex: 0,
  }}
/>

      {/* Colored segments between steps */}
      {totalExitSteps > 1 &&
        visibleExitSteps.map((step, index) => {
          if (index === totalExitSteps - 1) return null;
          const color = getStepColor(step);
          return (
            <div
              key={`${step.key}-segment-exit`}
              style={{
                position: "absolute",
                top: 21,
                left: `${segmentWidth * index}%`,
                width: `${segmentWidth}%`,
                height: 2,
                backgroundColor: color,
                zIndex: 1,
              }}
            />
          );
        })}

      {visibleExitSteps.map((step, index) => {
        // For exit flow, rely on backend 'completed' flag
        const visuallyCompleted = !!step.completed;
        const color = getStepColor(step);

        const isLwdStep = step.key === "last_working_day";
        const isExitInterviewStep = step.key === "exit_interview";
        const isExitInventoryStep = step.key === "exit_inventory";
        const isAccessRevokeStep = step.key === "access_revoke";
        const isRelievingDocsStep = step.key === "relieving_docs";
        const isExitCompleteStep = step.key === "exit_complete";

        const supportsManualOverride =
          isExitInterviewStep || isRelievingDocsStep;

        const handleExitStageClick = () => {
          if (isAccessRevokeStep) {
            const revokeIndex = visibleExitSteps.findIndex((s) => s.key === "access_revoke");
            if (revokeIndex > 0) {
              const hasPendingPrevious = visibleExitSteps
                .slice(0, revokeIndex)
                .some(
                  (s) => s.mandatory && !s.completed
                );

              if (hasPendingPrevious) {
                toast.error("Complete all previous mandatory exit stages first.");
                return;
              }
            }
            fetchAccessRevokeChecklist().then(() => {
              setTimeout(() => {
                const modalEl = document.getElementById("access_revoke_modal");
                if (modalEl) {
                  const bsModal = Modal.getInstance(modalEl) || new Modal(modalEl);
                  bsModal.show();
                }
              }, 50);
            });
          }
        };

        const displayLabel = isLwdStep
          ? "Last Working Day"
          : isExitInterviewStep
          ? "Exit Interview"
          : isExitInventoryStep
          ? "Inventory Clearance"
          : isAccessRevokeStep
          ? "Access Revoke"
          : isRelievingDocsStep
          ? "Relieving Docs"
          : isExitCompleteStep
          ? "Exit Complete"
          : step.label;

        const handleExitToggle = async () => {
          if (!supportsManualOverride) return;
          if (!employeeId) return;
          if (step.locked) {
            toast.error("Complete previous exit stages first.");
            return;
          }

          try {
            await nodeApi("/employee/exit/manual", {
              method: "POST",
              body: {
                userId: employeeId,
                stepKey: step.key,
              },
            });
            await onOnboardingRefresh?.();
            await fetchExitProgress();
          } catch (err: any) {
            const message =
              err?.response?.data?.error ||
              err?.message ||
              "Failed to update exit stage.";
            toast.error(message);
          }
        };

        return (
          <div
            key={step.key}
            className="text-center position-relative"
            style={{
minWidth: isMobileView ? 80 : "auto",
flex: isMobileView ? "0 0 auto" : "1",
  zIndex: 2,
  textAlign: "center",
}}
          >
            <div
              style={{
                width: isMobileView? 34 : 42,
                height: isMobileView? 34 : 42,
                borderRadius: "50%",
                backgroundColor: color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                color: "#fff",
                margin: "0 auto",
                boxShadow: visuallyCompleted
                  ? "0 0 10px rgba(34,197,94,0.4)"
                  : "none",
                cursor: isAccessRevokeStep ? "pointer" : "default",
              }}
              onClick={() => {
                if (isAccessRevokeStep) handleExitStageClick();
              }}
            >
              {visuallyCompleted ? "✓" : index + 1}
            </div>

<div
  style={{
    fontSize: isMobileView ? 11 : 13,
    marginTop: 8,
    ...(isMobileView
      ? {
          maxWidth: 80,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }
      : {}),
  }}
  title={displayLabel}
>
  {displayLabel}
</div>

           {/* LWD display */}
            {step.count !== undefined && (
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {step.key === "exit_inventory"
                  ? step.count
                  : `${step.count}/${step.required}`}
              </div>
            )}

            <div className="mt-2">
              {isAccessRevokeStep ? (
                <button
                  className="btn btn-icon btn-sm btn-outline-primary rounded-circle"
                  style={{ marginTop: "-4px", width: "26px", height: "26px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExitStageClick();
                  }}
                  title="View Checklist"
                >
                  <i className="ti ti-list-details" style={{ fontSize: "14px" }}></i>
                </button>
              ) : supportsManualOverride ? (
                <div className="form-check form-switch d-flex justify-content-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={visuallyCompleted}
                    // For exit stages, always allow manual toggle;
                    // backend will enforce any sequential locking.
                    disabled={false}
                    onChange={handleExitToggle}
                  />
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        );
      })}
    </div>
    </div>
      );
    })()
    )}

    {/* Mark exit complete */}
    {exitSummary && !exitSummary.isExitComplete && (
      <div className="mt-3">
        {exitSummary.isComplete ? (
          <button
            className="btn btn-danger px-4"
            onClick={async () => {
              if (!employeeId) return;
              try {
                await nodeApi("/employee/exit/complete", {
                  method: "POST",
                  body: { userId: employeeId },
                });
                await fetchExitProgress();
                if (skipSaveApiCall && onOnboardingRefresh) {
                  await onOnboardingRefresh();
                }
                toast.success("Exit marked complete");
              } catch (err: any) {
                const message =
                  err?.response?.data?.error ||
                  err?.message ||
                  "Failed to mark exit complete.";
                toast.error(message);
              }
            }}
          >
            Mark as Exited
          </button>
        ) : null}
      </div>
    )}
  </div>
)}

                  {/* Onboarding workflow – non‑exit statuses */}
                 {roleName === "1" &&
                  !isNoticePeriod() &&
                  visibleOnboardingSteps.length > 0 &&
                  !onboardingMarkedComplete &&
                  !(
                    onboardingSummary &&
                    (onboardingSummary.onboardingMarkedComplete ||
                      onboardingSummary.isComplete)
                  ) && (
  <div className="mt-4 px-3">
   <div
  className="position-relative"
  style={{
    overflowX: isMobileView ? "auto" : "visible",
    paddingBottom: 6,
  }}
>
      <div
    className="d-flex align-items-start flex-nowrap"
    style={{
      gap: isMobileView ? 16 : 0,
      minWidth: isMobileView ? "max-content" : "100%",
      position: "relative",
    }}
  >
      {/* Base neutral line across full width */}
    <div
  style={{
    position: "absolute",
    top: 21,
    left: isMobileView ? 20 : 0,
    right: isMobileView ? 20 : 0,
    height: 2,
    backgroundColor: "#dee2e6",
    zIndex: 0,
  }}
/>

      {/* Colored segments between steps, using each stage's color */}
      {totalOnboardingSteps > 1 &&
        visibleOnboardingSteps.map((step, index) => {
          if (index === totalOnboardingSteps - 1) return null;
          const color = getStepColor(step);
          return (
            <div
              key={`${step.key}-segment`}
              style={{
                position: "absolute",
                top: 21,
                left: `${segmentWidthPercent * index}%`,
                width: `${segmentWidthPercent}%`,
                height: 2,
                backgroundColor: color,
                zIndex: 1,
              }}
            />
          );
        })}

      {visibleOnboardingSteps.map((step, index) => {
        let visuallyCompleted = isStepVisuallyCompleted(step);

        if (step.key === "status") {
          visuallyCompleted = onboardingMarkedComplete;
        }
        const color = getStepColor(step);

        // Custom display labels for specific steps
        const displayLabel =
          step.key === "team"
            ? "TeamLogger Setup"
            : step.key === "status"
            ? "Onboarding Complete"
            : step.label;

        // Allow manual override for status, team logger, inventory assignment, and payroll setup
        const isStatusStep = step.key === "status";
        const isInventoryStep =
          typeof step.key === "string" &&
          step.key.toLowerCase().includes("inventory");
        // const inventoryNotAssigned =
        //   isInventoryStep && (!step.count || step.count === 0);
        const isPayrollStep = step.key === "payroll";
        const isTeamStep = step.key === "team";
        const isInductionStep = step.key === "induction";
        // const isDocumentStep = step.key === "documents";
        const supportsManualOverride =
          isStatusStep || isTeamStep || isInventoryStep || isPayrollStep;

        const handleStageClick = () => {
          if (step.key === "team") {
            toast.info(
              "Please SetUp Team Logger for this employee in the Team Logger system."
            );
          } else if (step.key === "payroll") {
            toast.info(
              "Please complete CTC and salary structure for this employee in the Payroll tab."
            );
          } else if (step.key === "induction") {
            const inductionIndex = visibleOnboardingSteps.findIndex((s) => s.key === "induction");
            if (inductionIndex > 0) {
              const hasPendingPrevious = visibleOnboardingSteps
                .slice(0, inductionIndex)
                .some(
                  (s) =>
                    s.mandatory &&
                    !(
                      typeof s.count === "number" &&
                      typeof s.required === "number" &&
                      s.required > 0 &&
                      s.count === s.required
                    ) &&
                    !s.completed
                );

              if (hasPendingPrevious) {
                toast.error(
                  "Complete all previous mandatory onboarding stages before induction."
                );
                return;
              }
            }
            fetchInductionChecklist().then(() => {
              setTimeout(() => {
                const modalEl = document.getElementById("induction_modal");
                if (modalEl) {
                  const bsModal = Modal.getInstance(modalEl) || new Modal(modalEl);
                  bsModal.show();
                }
              }, 50);
            });
          }
        };

        return (
          <div
            key={step.key}
            className="text-center position-relative"
           style={{
minWidth: isMobileView ? 80 : "auto",
flex: isMobileView ? "0 0 auto" : "1",
  zIndex: 2,
  textAlign: "center",
}}
          >
            <div
              style={{
                width: isMobileView? 34 : 42,
                height: isMobileView? 34 : 42,
                borderRadius: "50%",
                backgroundColor: color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                color: "#fff",
                margin: "0 auto",
                boxShadow: step.completed
                  ? "0 0 10px rgba(34,197,94,0.4)"
                  : "none",
                cursor: "pointer",
              }}
              onClick={handleStageClick}
            >
              {visuallyCompleted ? "✓" : index + 1}
            </div>

<div
  style={{
    fontSize: isMobileView ? 11 : 13,
    marginTop: 8,
    ...(isMobileView
      ? {
          maxWidth: 80,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }
      : {}),
  }}
  title={displayLabel}
>
  {displayLabel}
</div>

            {step.count !== undefined && (
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {step.count}/{step.required}
              </div>
            )}

            <div className="mt-2">
              {isInductionStep ? (
                <button
                  className="btn btn-icon btn-sm btn-outline-primary rounded-circle"
                  style={{ marginTop: "-4px", width: "26px", height: "26px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStageClick();
                  }}
                  title="View Checklist"
                >
                  <i className="ti ti-list-details" style={{ fontSize: "14px" }}></i>
                </button>
              ) : supportsManualOverride ? (
                <div className="form-check form-switch d-flex justify-content-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={visuallyCompleted}
                    disabled={false}
                    onChange={async () => {
                      let currentPassword = password;
                      // Frontend guard: do not allow status change
                      // until all previous mandatory stages are complete,
                      // and ensure a password exists before final completion.

                      //  if (inventoryNotAssigned) {
                      //     toast.info("Assign at least one inventory before completing this stage.");
                      //     return;
                      //   }
                      if (isStatusStep) {
                        const statusIndex = visibleOnboardingSteps.findIndex(
                          (s) => s.key === "status"
                        );
                        if (statusIndex > 0) {
                          const hasPendingPrevious = visibleOnboardingSteps
                            .slice(0, statusIndex)
                            .some(
                              (s) =>
                                s.mandatory &&
                                !(
                                  typeof s.count === "number" &&
                                  typeof s.required === "number" &&
                                  s.required > 0 &&
                                  s.count === s.required
                                ) &&
                                !s.completed
                            );

                          if (hasPendingPrevious) {
                            toast.error(
                              "Complete all previous mandatory onboarding stages before changing status."
                            );
                            return;
                          }
                        }

                        // Auto-generate password if missing when marking the final step complete
                        if (!visuallyCompleted && !currentPassword) {
                          try {
                            const pwdRes = await axiosInstance.get("/employees/generatepassword");
                            // Adjust mapping to how your axios response unwraps
                            currentPassword = pwdRes.data?.password || pwdRes.data;
                          } catch (pwdErr) {
                            toast.error("Failed to auto-generate password. Please try again.");
                            return;
                          }
                        }
                      }

                      try {
                        const body: any = {
                          userId: employeeId,
                          stepKey: step.key,
                        };

                        // When marking the final status step complete and a
                        // frontend‑generated password is available, send it so
                        // the backend can hash and email HRMS credentials.
                        if (isStatusStep && !visuallyCompleted && currentPassword) {
                          body.password = currentPassword;
                        }

                        await nodeApi("/employee/onboarding/manual", {
                          method: "POST",
                          body,
                        });

                        if (isStatusStep && !visuallyCompleted && onOnboardingComplete) {
                          onOnboardingComplete(currentPassword);
                        }

                        if (skipSaveApiCall && onOnboardingRefresh) {
                          await onOnboardingRefresh();
                        } else {
                          await fetchOnboarding();
                        }
                        await fetchInductionChecklist();
                      } catch (err: any) {
                        const message =
                          err?.response?.data?.error ||
                          err?.message ||
                          "Failed to update onboarding step.";
                        toast.error(message);
                      }
                    }}
                  />
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        );
      })}
    </div>
    </div>
  </div>
)}

                </>
              
            </div>
          </div>
        )}
      </div>

      {/* Crop Modal */}
      <div
        ref={modalRef}
        className="modal fade"
        id="edit_profile_photo"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Profile Photo</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body text-center">
              {selectedFile && (
                <>
                  <AvatarEditor
                    ref={editorRef}
                    image={selectedFile}
                    width={250}
                    height={250}
                    border={50}
                    borderRadius={125}
                    color={[255, 255, 255, 0.6]}
                    scale={scale}
                    rotate={0}
                  />
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={scale}
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                    className="mt-3 w-100"
                  />
                </>
              )}
            </div>
            <div className="modal-footer">
              <Button data-bs-dismiss="modal">Cancel</Button>
              <Button type="primary" onClick={handleSaveCrop}>
                Save &amp; Crop
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Induction Checklist Modal */}
      <div
        className="modal fade"
        id="induction_modal"
        tabIndex={-1}
        aria-hidden="true"
        
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header bg-light border-bottom-0 pb-3 pt-4 px-4">
              <h5 className="modal-title d-flex align-items-center flex-wrap text-dark fw-semibold">
                <i className="ti ti-clipboard-list me-2 fs-3 text-primary"></i>
                Induction Checklist
                {localInductionData.length > 0 && (
                  <span className="badge bg-primary ms-3 fw-medium" style={{ fontSize: "12px", borderRadius: "12px" }}>
                    {localInductionData.filter((i) => !!i.completed).length} / {localInductionData.length} completed
                  </span>
                )}
              </h5>
              <button 
                type="button" 
                className="btn-close shadow-none" 
                data-bs-dismiss="modal" 
                aria-label="Close" 
                onClick={() => setLocalInductionData(inductionData)}
              ></button>
            </div>
            <div className="modal-body px-4 pt-2 pb-4 text-start bg-light">
              {inductionLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : localInductionData.length === 0 ? (
                <div className="text-center py-5 text-muted bg-white rounded-3 border">
                  No induction items found.
                </div>
              ) : (
                <div className="list-group list-group-flush rounded-3 border shadow-sm">
                  {localInductionData.map((item, idx) => (
                    <label 
                      key={item.id} 
                      className={`list-group-item d-flex justify-content-between align-items-center py-3 ${idx !== localInductionData.length - 1 ? "border-bottom" : "border-0"}`}
                      style={{ cursor: "pointer", transition: "background-color 0.2s"
         }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                    >
                     <div
                      className="pe-3"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                       <span
                        className="mb-0 text-dark fw-medium"
                        style={{
                          fontSize: "14px",
                          display: "block",
                          width: "100%",
                          maxWidth: "100%",
                          whiteSpace: "normal",      // allow wrapping
                          wordBreak: "break-word",   // break long words
                          overflowWrap: "anywhere",  // prevent overflow
                          lineHeight: "1.4",
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </span>
                      </div>
                      <div className="form-check form-switch mb-0 ms-auto flex-shrink-0">
                        <input
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          role="switch"
                          style={{ cursor: "pointer", width: "36px", height: "18px" }}
                          checked={!!item.completed}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setLocalInductionData((prev) =>
                              prev.map((i) => (i.id === item.id ? { ...i, completed: checked } : i))
                            );
                          }}
                        />
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer border-top-0 pt-0 px-4 pb-4 bg-light">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={() => setLocalInductionData(inductionData)}
                disabled={isSubmittingInduction}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                disabled={isSubmittingInduction || localInductionData.length === 0}
                onClick={async () => {
                  setIsSubmittingInduction(true);
                  const updates: { checklistId: number; value: number }[] = [];
                  
                  localInductionData.forEach((localItem) => {
                    const originalItem = inductionData.find((i) => i.id === localItem.id);
                    if (originalItem && !!originalItem.completed !== !!localItem.completed) {
                      updates.push({ checklistId: localItem.id, value: localItem.completed ? 1 : 0 });
                    }
                  });
                  
                  if (updates.length > 0) {
                    try {
                      await nodeApi("/employee/induction-update", {
                        method: "POST",
                        body: { userId: employeeId, updates },
                      });
                      toast.success("Induction checklist saved successfully.");
                      await fetchInductionChecklist();
                      if (skipSaveApiCall && onOnboardingRefresh) {
                        await onOnboardingRefresh();
                      } else {
                        await fetchOnboarding();
                      }
                      const modalEl = document.getElementById("induction_modal");
                      if (modalEl) {
                        const bsModal = Modal.getInstance(modalEl);
                        bsModal?.hide();
                      }
                    } catch (err: any) {
                      toast.error(err?.message || "Failed to save checklist.");
                    }
                  } else {
                    const modalEl = document.getElementById("induction_modal");
                    if (modalEl) {
                      const bsModal = Modal.getInstance(modalEl);
                      bsModal?.hide();
                    }
                  }
                  setIsSubmittingInduction(false);
                }}
              >
                {isSubmittingInduction ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save Checklist"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Access Revoke Checklist Modal */}
      <div
        className="modal fade"
        id="access_revoke_modal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header bg-light border-bottom-0 pb-3 pt-4 px-4">
              <h5 className="modal-title d-flex align-items-center flex-wrap text-dark fw-semibold">
                <i className="ti ti-clipboard-list me-2 fs-3 text-primary"></i>
                Revoke Checklist
                {localAccessRevokeData.length > 0 && (
                  <span className="badge bg-primary ms-3 fw-medium" style={{ fontSize: "12px", borderRadius: "12px" }}>
                    {localAccessRevokeData.filter((i) => !!i.completed).length} / {localAccessRevokeData.length} completed
                  </span>
                )}
              </h5>
              <button 
                type="button" 
                className="btn-close shadow-none" 
                data-bs-dismiss="modal" 
                aria-label="Close" 
                onClick={() => setLocalAccessRevokeData(accessRevokeData)}
              ></button>
            </div>
            <div className="modal-body px-4 pt-2 pb-4 text-start bg-light">
              {accessRevokeLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : localAccessRevokeData.length === 0 ? (
                <div className="text-center py-5 text-muted bg-white rounded-3 border">
                  No access revoke items found.
                </div>
              ) : (
                <div className="list-group list-group-flush rounded-3 border shadow-sm">
                  {localAccessRevokeData.map((item, idx) => (
                    <label 
                      key={item.id} 
                      className={`list-group-item d-flex justify-content-between align-items-center py-3 ${idx !== localAccessRevokeData.length - 1 ? "border-bottom" : "border-0"}`}
                      style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                    >
                     <div
                      className="pe-3"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                       <span
                        className="mb-0 text-dark fw-medium"
                        style={{
                          fontSize: "14px",
                          display: "block",
                          width: "100%",
                          maxWidth: "100%",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          lineHeight: "1.4",
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </span>
                      </div>
                      <div className="form-check form-switch mb-0 ms-auto flex-shrink-0">
                        <input
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          role="switch"
                          style={{ cursor: "pointer", width: "36px", height: "18px" }}
                          checked={!!item.completed}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setLocalAccessRevokeData((prev) =>
                              prev.map((i) => (i.id === item.id ? { ...i, completed: checked } : i))
                            );
                          }}
                        />
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer border-top-0 pt-0 px-4 pb-4 bg-light">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={() => setLocalAccessRevokeData(accessRevokeData)}
                disabled={isSubmittingAccessRevoke}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                disabled={isSubmittingAccessRevoke || localAccessRevokeData.length === 0}
                onClick={async () => {
                  setIsSubmittingAccessRevoke(true);
                  const updates: { checklistId: number; value: number }[] = [];
                  
                  localAccessRevokeData.forEach((localItem) => {
                    const originalItem = accessRevokeData.find((i) => i.id === localItem.id);
                    if (originalItem && !!originalItem.completed !== !!localItem.completed) {
                      updates.push({ checklistId: localItem.id, value: localItem.completed ? 1 : 0 });
                    }
                  });
                  
                  if (updates.length > 0) {
                    try {
                      await nodeApi("/employee/access-revoke-update", {
                        method: "POST",
                        body: { userId: employeeId, updates },
                      });
                      toast.success("Access Revoke checklist saved successfully.");
                      await fetchAccessRevokeChecklist();
                      if (skipSaveApiCall && onOnboardingRefresh) {
                        await onOnboardingRefresh();
                      } else {
                        await fetchExitProgress();
                      }
                      
                      const modalEl = document.getElementById("access_revoke_modal");
                      if (modalEl) {
                        const bsModal = Modal.getInstance(modalEl);
                        bsModal?.hide();
                      }
                    } catch (err: any) {
                      toast.error(err?.message || "Failed to save checklist.");
                    }
                  } else {
                    const modalEl = document.getElementById("access_revoke_modal");
                    if (modalEl) {
                      const bsModal = Modal.getInstance(modalEl);
                      bsModal?.hide();
                    }
                  }
                  setIsSubmittingAccessRevoke(false);
                }}
              >
                {isSubmittingAccessRevoke ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save Checklist"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Additional fields tabs if needed... */}
    </>
  );
};

export default EmployeeImage;
